import { FormattedUsageDataPoint, UsageStats } from '../types';

/**
 * Calculates statistics from formatted usage data
 *
 * Computes total, maximum, and average AI units across all data points and groups.
 *
 * @param formattedUsageData Array of formatted data points
 * @param groups Array of group names present in the data
 * @returns Object containing calculated statistics
 */
export function calculateUsageStats(
  formattedUsageData: FormattedUsageDataPoint[] | undefined,
  groups: string[]
): UsageStats {
  if (!formattedUsageData || formattedUsageData.length === 0) {
    return {
      totalAiUnits: 0,
      maxAiUnits: 0,
      averageAiUnits: 0,
      uniqueCount: groups.length,
    };
  }

  // Calculate total AI units across all data points and groups
  const totalAiUnits = formattedUsageData.reduce((sum, dataPoint) => {
    const periodTotal = groups.reduce((periodSum, group) => {
      const value = dataPoint[group];
      const numericValue = typeof value === 'number' ? value : 0;
      return periodSum + numericValue;
    }, 0);
    return sum + periodTotal;
  }, 0);

  // Calculate maximum AI units for any single data point
  const maxAiUnits = formattedUsageData.reduce((max, dataPoint) => {
    const periodTotal = groups.reduce((periodSum, group) => {
      const value = dataPoint[group];
      const numericValue = typeof value === 'number' ? value : 0;
      return periodSum + numericValue;
    }, 0);
    return Math.max(max, periodTotal);
  }, 0);

  // Calculate average AI units per data point
  const averageAiUnits = totalAiUnits / formattedUsageData.length;

  return {
    totalAiUnits,
    maxAiUnits,
    averageAiUnits,
    uniqueCount: groups.length,
  };
}
